import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  FEATURES,
  LIMIT_DEFS,
  TENANT_STATUS_DEFS,
  TENANT_STATUS_MAP,
  type TenantStatus,
} from '@goyone/shared';
import { date, dateTime, money } from '@ui/lib/format';
import { Badge, ErrorState, Modal, PageHead, Stat } from '@ui/components/ui';
import { Icon } from '@ui/components/Icon';
import { api } from '../lib/api';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  email: string | null;
  phone: string | null;
  status: TenantStatus;
  trialEndsAt: string | null;
  suspendedAt: string | null;
  cancelledAt: string | null;
  purgeAt: string | null;
  createdAt: string;
  subscription: {
    id: string;
    status: string;
    cycle: string;
    price: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    limitOverrides: Record<string, number>;
    featureOverrides: string[];
    plan: { id: string; code: string; name: string; features: string[]; limits: Record<string, number> };
  } | null;
  platformBills: Array<{
    id: string;
    number: string;
    total: string;
    status: string;
    issuedAt: string;
    dueAt: string;
    paidAt: string | null;
  }>;
  members: Array<{
    id: string;
    userId: string;
    role: string;
    status: string;
    name: string;
    email: string;
    lastLoginAt: string | null;
  }>;
}

const ROLE_LABELS: Record<string, string> = {
  OWNER: 'مالك',
  ADMIN: 'مدير النظام',
  MANAGER: 'مدير',
  SALES: 'مبيعات',
  EMPLOYEE: 'موظف',
};

export function TenantDetail() {
  const { id = '' } = useParams();
  const queryClient = useQueryClient();
  const [dialog, setDialog] = useState<
    'plan' | 'renew' | 'status' | 'overrides' | 'invoice' | 'activate' | null
  >(null);
  const [resetting, setResetting] = useState<Tenant['members'][number] | null>(null);

  const tenant = useQuery({
    queryKey: ['tenant', id],
    queryFn: () => api<Tenant>(`/tenants/${id}`),
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['tenant', id] });
    void queryClient.invalidateQueries({ queryKey: ['tenants'] });
    void queryClient.invalidateQueries({ queryKey: ['summary'] });
    setDialog(null);
  };

  const t = tenant.data;
  const sub = t?.subscription;

  // الوحدات المتاحة = وحدات الباقة زائد ما مُنح فوقها.
  const activeFeatures = new Set([...(sub?.plan.features ?? []), ...(sub?.featureOverrides ?? [])]);

  return (
    <div className="page">
      <PageHead
        title={t ? t.name : 'مستأجر'}
        subtitle={t ? `${t.slug}.goyone.com` : undefined}
        actions={
          <>
            <Link to="/tenants" className="btn">
              <Icon name="arrowLeft" size={15} />
              رجوع
            </Link>
            {t && (
              <>
                {/*
                  فتح التطبيق بنطاق الشركة.
                  محليًا لا توجد نطاقات فرعية، فيُمرَّر النطاق كمعامل — وهو
                  ما يتيح تجربة الشركة المهيّأة فورًا بدل كتابته يدويًا.
                */}
                <a
                  className="btn"
                  href={appUrlFor(t.slug)}
                  target="_blank"
                  rel="noreferrer"
                >
                  <Icon name="arrowLeft" size={15} />
                  فتح التطبيق
                </a>
                <button className="btn" onClick={() => setDialog('status')}>
                  تغيير الحالة
                </button>
                {t.status === 'ACTIVE' ? (
                  <button className="btn btn-primary" onClick={() => setDialog('renew')}>
                    تجديد
                  </button>
                ) : (
                  <button className="btn btn-primary" onClick={() => setDialog('activate')}>
                    تفعيل الاشتراك
                  </button>
                )}
              </>
            )}
          </>
        }
      />

      {tenant.isError && <ErrorState error={tenant.error} onRetry={() => void tenant.refetch()} />}
      {tenant.isPending && <div className="skeleton" style={{ height: 300 }} />}

      {t && (
        <>
          <div className="stats">
            <Stat
              label="الحالة"
              value={
                <Badge kind={TENANT_STATUS_MAP[t.status].kind}>
                  {TENANT_STATUS_MAP[t.status].label}
                </Badge>
              }
              note={
                t.status === 'TRIAL' && t.trialEndsAt
                  ? `التجربة حتى ${date(t.trialEndsAt)}`
                  : t.status === 'CANCELLED' && t.purgeAt
                    ? `الحذف النهائي ${date(t.purgeAt)}`
                    : t.suspendedAt
                      ? `موقوف منذ ${date(t.suspendedAt)}`
                      : undefined
              }
            />
            <Stat
              label="الباقة"
              value={sub?.plan.name ?? '—'}
              note={sub ? money(sub.price) : undefined}
            />
            <Stat
              label="الاشتراك حتى"
              value={<span className="num">{sub ? date(sub.currentPeriodEnd) : '—'}</span>}
            />
            <Stat label="المستخدمون" value={t.members.length} />
          </div>

          <div className="builder">
            {/* ---- الاشتراك ---- */}
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">الاشتراك</h2>
                <div className="row" style={{ gap: 4 }}>
                  <button className="btn btn-sm" onClick={() => setDialog('plan')}>
                    تغيير الباقة
                  </button>
                  <button className="btn btn-sm" onClick={() => setDialog('overrides')}>
                    الاستثناءات
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div className="form-grid">
                  <div className="field">
                    <span className="label">دورة الفوترة</span>
                    <span>{sub?.cycle === 'YEARLY' ? 'سنوية' : 'شهرية'}</span>
                  </div>
                  <div className="field">
                    <span className="label">بداية المدة</span>
                    <span className="num">{sub ? date(sub.currentPeriodStart) : '—'}</span>
                  </div>
                </div>

                <h4 className="section-title">الوحدات المتاحة</h4>
                <div className="row" style={{ gap: 6, flexWrap: 'wrap' }}>
                  {FEATURES.map((f) => {
                    const on = activeFeatures.has(f.key);
                    const extra = sub?.featureOverrides.includes(f.key);
                    return (
                      <Badge key={f.key} kind={on ? (extra ? 'info' : 'success') : 'neutral'}>
                        {f.label}
                        {extra ? ' (إضافية)' : ''}
                      </Badge>
                    );
                  })}
                </div>

                <h4 className="section-title">الحدود</h4>
                <div className="table-wrap">
                  <table className="table">
                    <thead>
                      <tr>
                        <th>الحد</th>
                        <th style={{ width: 110 }}>الباقة</th>
                        <th style={{ width: 110 }}>الممنوح</th>
                      </tr>
                    </thead>
                    <tbody>
                      {LIMIT_DEFS.map((l) => {
                        const base = sub?.plan.limits?.[l.key];
                        const override = sub?.limitOverrides?.[l.key];
                        const show = (v: number | undefined) =>
                          v === undefined ? '—' : v === -1 ? 'بلا حد' : v;
                        return (
                          <tr key={l.key}>
                            <td className="cell-strong">{l.label}</td>
                            <td className="num cell-mute">{show(base)}</td>
                            <td className="num">
                              {override !== undefined ? (
                                <span className="pos">{show(override)}</span>
                              ) : (
                                '—'
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* ---- المستخدمون ---- */}
            <div className="card">
              <div className="card-head">
                <h2 className="card-title">المستخدمون</h2>
              </div>
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>الاسم</th>
                      <th>الدور</th>
                      <th>آخر دخول</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {t.members.map((m) => (
                      <tr key={m.id}>
                        <td>
                          <span className="cell-strong">{m.name}</span>
                          <div className="tiny mute num">{m.email}</div>
                        </td>
                        <td>
                          <Badge kind={m.status === 'ACTIVE' ? 'neutral' : 'danger'}>
                            {ROLE_LABELS[m.role] ?? m.role}
                          </Badge>
                        </td>
                        <td className="num cell-mute">
                          {m.lastLoginAt ? dateTime(m.lastLoginAt) : 'لم يدخل بعد'}
                        </td>
                        <td>
                          <button className="btn btn-sm" onClick={() => setResetting(m)}>
                            إعادة ضبط كلمة المرور
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ---- الفواتير ---- */}
          <div className="card mt-4">
            <div className="card-head">
              <h2 className="card-title">فواتير الاشتراك</h2>
              <button className="btn btn-sm btn-primary" onClick={() => setDialog('invoice')}>
                إصدار فاتورة
              </button>
            </div>
            <div className="table-wrap">
              <table className="table">
                <thead>
                  <tr>
                    <th>الرقم</th>
                    <th>الإصدار</th>
                    <th>الاستحقاق</th>
                    <th>القيمة</th>
                    <th>الحالة</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {t.platformBills.map((b) => (
                    <InvoiceRow key={b.id} invoice={b} onPaid={refresh} />
                  ))}
                </tbody>
              </table>
            </div>
            {t.platformBills.length === 0 && (
              <p className="small mute" style={{ padding: '12px 16px' }}>
                لا فواتير صادرة لهذا المستأجر بعد.
              </p>
            )}
          </div>
        </>
      )}

      {dialog === 'status' && t && (
        <StatusDialog tenant={t} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'plan' && t && (
        <PlanDialog tenant={t} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'renew' && t && (
        <RenewDialog tenant={t} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'activate' && t && (
        <ActivateDialog tenant={t} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'overrides' && t && sub && (
        <OverridesDialog tenant={t} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {dialog === 'invoice' && t && (
        <InvoiceDialog tenant={t} onClose={() => setDialog(null)} onDone={refresh} />
      )}
      {resetting && t && (
        <ResetPasswordDialog
          tenant={t}
          member={resetting}
          onClose={() => setResetting(null)}
          onDone={refresh}
        />
      )}
    </div>
  );
}

/* ========================================================================== */

/**
 * رابط تطبيق الشركة.
 *
 * في الإنتاج نطاق فرعي، ومحليًا معامل في الرابط. الفرق مخفي هنا في مكان
 * واحد بدل تكراره في كل موضع يحتاج الرابط.
 */
function appUrlFor(slug: string): string {
  const { protocol, hostname, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return `${protocol}//${hostname}:5173/?tenant=${encodeURIComponent(slug)}`;
  }
  const root = hostname.split('.').slice(-2).join('.');
  return `${protocol}//${slug}.${root}${port ? `:${port}` : ''}`;
}

function ActivateDialog({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: () => void;
}) {
  const [months, setMonths] = useState('1');

  const activate = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant.id}/activate`, {
        method: 'POST',
        body: { months: Number(months) },
      }),
    onSuccess: onDone,
  });

  const end = new Date();
  end.setMonth(end.getMonth() + (Number(months) || 1));

  return (
    <Modal
      title="تفعيل الاشتراك"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => activate.mutate()}
            disabled={activate.isPending}
          >
            {activate.isPending ? 'جارٍ التفعيل' : 'تفعيل'}
          </button>
        </>
      }
    >
      {activate.isError && (
        <div className="alert alert-danger mb-4">
          {activate.error instanceof Error ? activate.error.message : 'تعذّر التفعيل.'}
        </div>
      )}

      <p className="small mute mb-4">
        التفعيل ينهي الفترة التجريبية ويفتح مدة فوترة تبدأ اليوم. الشركة تعود إلى العمل
        الكامل فورًا إن كانت موقوفة.
      </p>

      <div className="field">
        <label className="label" htmlFor="a-months">
          مدة الاشتراك بالأشهر
        </label>
        <select
          id="a-months"
          className="select"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
        >
          <option value="1">شهر</option>
          <option value="3">ثلاثة أشهر</option>
          <option value="6">ستة أشهر</option>
          <option value="12">سنة</option>
        </select>
      </div>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span className="mute">الحالة الآن</span>
          <span>{TENANT_STATUS_MAP[tenant.status].label}</span>
        </div>
        <div className="totals-row">
          <span className="mute">قيمة الاشتراك</span>
          <span className="num">
            {tenant.subscription ? money(tenant.subscription.price) : '—'}
          </span>
        </div>
        <div className="totals-row totals-final">
          <span>المدة تنتهي في</span>
          <span className="num">{date(end)}</span>
        </div>
      </div>

      <div className="alert alert-info mt-4">
        التفعيل لا يُصدر فاتورة تلقائيًا. أصدرها من قسم الفواتير أسفل الصفحة إن كنت تفوتر
        هذا العميل.
      </div>
    </Modal>
  );
}

function InvoiceRow({
  invoice,
  onPaid,
}: {
  invoice: Tenant['platformBills'][number];
  onPaid: () => void;
}) {
  const pay = useMutation({
    mutationFn: () => api(`/invoices/${invoice.id}/pay`, { method: 'POST', body: {} }),
    onSuccess: onPaid,
  });

  const overdue = invoice.status === 'open' && new Date(invoice.dueAt) < new Date();

  return (
    <tr>
      <td className="num cell-strong">{invoice.number}</td>
      <td className="num cell-mute">{date(invoice.issuedAt)}</td>
      <td className={`num ${overdue ? 'neg' : 'cell-mute'}`}>{date(invoice.dueAt)}</td>
      <td className="num">{money(invoice.total)}</td>
      <td>
        <Badge kind={invoice.status === 'paid' ? 'success' : overdue ? 'danger' : 'info'}>
          {invoice.status === 'paid' ? 'مسدَّدة' : overdue ? 'متأخرة' : 'مفتوحة'}
        </Badge>
      </td>
      <td className="end">
        {invoice.status === 'open' && (
          <button
            className="btn btn-sm"
            onClick={() => pay.mutate()}
            disabled={pay.isPending}
          >
            تسجيل سداد
          </button>
        )}
      </td>
    </tr>
  );
}

function StatusDialog({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: () => void;
}) {
  const [status, setStatus] = useState<TenantStatus>(tenant.status);

  const save = useMutation({
    mutationFn: () => api(`/tenants/${tenant.id}`, { method: 'PATCH', body: { status } }),
    onSuccess: onDone,
  });

  const def = TENANT_STATUS_MAP[status];

  return (
    <Modal
      title="تغيير حالة المستأجر"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={status === tenant.status || save.isPending}
          >
            حفظ
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="s-status">
          الحالة
        </label>
        <select
          id="s-status"
          className="select"
          value={status}
          onChange={(e) => setStatus(e.target.value as TenantStatus)}
        >
          {TENANT_STATUS_DEFS.map((s) => (
            <option key={s.key} value={s.key}>
              {s.label}
            </option>
          ))}
        </select>
      </div>

      <div className={`alert mt-4 ${def.access === 'full' ? 'alert-info' : 'alert-warning'}`}>
        {def.access === 'full'
          ? 'المستأجر يعمل بكامل صلاحياته: قراءة وكتابة.'
          : 'المستأجر يقرأ بياناته ولا يكتب. لا تُحذف بياناته، لكن العمل يتوقف.'}
      </div>

      {status === 'CANCELLED' && (
        <div className="alert alert-danger mt-2">
          الإلغاء يحدد موعدًا للحذف النهائي بعد مدة الاحتفاظ. العودة إلى حالة عاملة قبل ذلك
          الموعد تُلغي الحذف.
        </div>
      )}
    </Modal>
  );
}

function PlanDialog({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: () => void;
}) {
  const [planCode, setPlanCode] = useState(tenant.subscription?.plan.code ?? '');
  const [price, setPrice] = useState(
    tenant.subscription ? String(Number(tenant.subscription.price)) : '',
  );
  const [custom, setCustom] = useState(false);

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<Array<{ id: string; code: string; name: string; price: string }>>('/plans'),
  });

  const save = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant.id}/plan`, {
        method: 'POST',
        body: { planCode, ...(custom ? { price: Number(price) || 0 } : {}) },
      }),
    onSuccess: onDone,
  });

  return (
    <Modal
      title="تغيير الباقة"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!planCode || save.isPending}
          >
            حفظ
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="p-plan">
          الباقة
        </label>
        <select
          id="p-plan"
          className="select"
          value={planCode}
          onChange={(e) => {
            setPlanCode(e.target.value);
            const p = plans.data?.find((x) => x.code === e.target.value);
            if (p && !custom) setPrice(String(Number(p.price)));
          }}
        >
          {plans.data?.map((p) => (
            <option key={p.id} value={p.code}>
              {p.name} — {money(p.price)}
            </option>
          ))}
        </select>
      </div>

      <label className="row mt-4" style={{ cursor: 'pointer' }}>
        <input type="checkbox" checked={custom} onChange={(e) => setCustom(e.target.checked)} />
        <span className="small">سعر متفق عليه يخالف سعر الباقة</span>
      </label>

      {custom && (
        <div className="field mt-2">
          <label className="label" htmlFor="p-price">
            السعر
          </label>
          <input
            id="p-price"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
          <span className="tiny mute">
            يُحفظ في الاشتراك ولا يتأثر بتعديل سعر الباقة لاحقًا.
          </span>
        </div>
      )}

      <div className="alert alert-info mt-4">
        تغيير الباقة يسري فورًا على الوحدات والحدود، ولا يغيّر تاريخ انتهاء المدة الحالية.
      </div>
    </Modal>
  );
}

function RenewDialog({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: () => void;
}) {
  const [months, setMonths] = useState('1');

  const renew = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant.id}/renew`, { method: 'POST', body: { months: Number(months) } }),
    onSuccess: onDone,
  });

  const current = tenant.subscription?.currentPeriodEnd;
  const base = current && new Date(current) > new Date() ? new Date(current) : new Date();
  const next = new Date(base);
  next.setMonth(next.getMonth() + (Number(months) || 1));

  return (
    <Modal
      title="تجديد الاشتراك"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => renew.mutate()}
            disabled={renew.isPending}
          >
            {renew.isPending ? 'جارٍ التجديد' : 'تجديد'}
          </button>
        </>
      }
    >
      {renew.isError && (
        <div className="alert alert-danger mb-4">
          {renew.error instanceof Error ? renew.error.message : 'تعذّر التجديد.'}
        </div>
      )}

      <div className="field">
        <label className="label" htmlFor="r-months">
          مدة التجديد بالأشهر
        </label>
        <select
          id="r-months"
          className="select"
          value={months}
          onChange={(e) => setMonths(e.target.value)}
        >
          <option value="1">شهر</option>
          <option value="3">ثلاثة أشهر</option>
          <option value="6">ستة أشهر</option>
          <option value="12">سنة</option>
        </select>
      </div>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span className="mute">ينتهي حاليًا</span>
          <span className="num">{current ? date(current) : '—'}</span>
        </div>
        <div className="totals-row totals-final">
          <span>ينتهي بعد التجديد</span>
          <span className="num">{date(next)}</span>
        </div>
      </div>

      <div className="alert alert-info mt-4">
        التجديد يبدأ من نهاية المدة الحالية لا من اليوم، فلا يضيع ما تبقّى منها. والمستأجر
        الموقوف يعود إلى العمل بالتجديد.
      </div>
    </Modal>
  );
}

/**
 * إعادة ضبط كلمة مرور عضو.
 *
 * لا يوجد في النظام "نسيت كلمة المرور" بالبريد، ولا أحد فوق المالك داخل
 * شركته. من ينسى كلمته يبقى خارج نظامه حتى يعيدها فريق المنصة من هنا.
 */
function ResetPasswordDialog({
  tenant,
  member,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  member: Tenant['members'][number];
  onClose: () => void;
  onDone: () => void;
}) {
  const [password, setPassword] = useState('');
  const [done, setDone] = useState(false);

  const reset = useMutation({
    mutationFn: () =>
      api<{ revokedSessions: number }>(`/tenants/${tenant.id}/reset-password`, {
        method: 'POST',
        body: { userId: member.userId, password },
      }),
    onSuccess: () => setDone(true),
  });

  if (done) {
    return (
      <Modal
        title="أُعيد ضبط كلمة المرور"
        onClose={onDone}
        footer={
          <button className="btn btn-primary" onClick={onDone}>
            تم
          </button>
        }
      >
        <div className="alert alert-warning">
          <span className="strong">سلّمها عبر قناة آمنة.</span> لا تُرسل كلمة المرور في بريد
          عادي أو رسالة نصية، ونبّه صاحبها ليغيّرها من شاشة حسابه بعد أول دخول.
        </div>

        <div className="totals-box mt-4">
          <div className="totals-row">
            <span className="mute">الحساب</span>
            <span className="num">{member.email}</span>
          </div>
          <div className="totals-row">
            <span className="mute">النطاق</span>
            <span className="num">{tenant.slug}</span>
          </div>
          <div className="totals-row totals-final">
            <span>كلمة المرور</span>
            <span className="num">{password}</span>
          </div>
        </div>

        {(reset.data?.revokedSessions ?? 0) > 0 && (
          <p className="tiny mute mt-3">
            أُغلقت {reset.data?.revokedSessions} من جلساته المفتوحة.
          </p>
        )}
      </Modal>
    );
  }

  return (
    <Modal
      title="إعادة ضبط كلمة المرور"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-danger"
            onClick={() => reset.mutate()}
            disabled={password.length < 12 || reset.isPending}
          >
            {reset.isPending ? 'جارٍ الضبط' : 'إعادة الضبط'}
          </button>
        </>
      }
    >
      {reset.isError && (
        <div className="alert alert-danger mb-4">
          {reset.error instanceof Error ? reset.error.message : 'تعذّرت إعادة الضبط.'}
        </div>
      )}

      <div className="alert alert-warning mb-4">
        كلمة المرور الحالية لـ <span className="strong">{member.name}</span> ستُلغى، وتُغلق كل
        جلساته المفتوحة. تأكّد أن الطلب جاء منه فعلًا قبل المتابعة.
      </div>

      <div className="field">
        <label className="label req" htmlFor="rp-password">
          كلمة المرور الجديدة
        </label>
        <input
          id="rp-password"
          className="input num"
          dir="ltr"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="اثنا عشر حرفًا على الأقل"
        />
        <button
          className="btn btn-sm mt-2"
          type="button"
          onClick={() => setPassword(suggestPassword())}
        >
          توليد كلمة قوية
        </button>
      </div>
    </Modal>
  );
}

/**
 * كلمة مرور مؤقّتة.
 *
 * من يكتبها بنفسه يميل إلى المكرّر والقصير، وهي تمرّ في يد ثالثة قبل وصولها
 * لصاحبها. التوليد من crypto لا من Math.random: هذه كلمة تفتح شركة كاملة.
 */
function suggestPassword(): string {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const bytes = crypto.getRandomValues(new Uint32Array(16));
  return Array.from(bytes, (b) => alphabet[b % alphabet.length]).join('');
}

function OverridesDialog({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: () => void;
}) {
  const sub = tenant.subscription!;
  const [limits, setLimits] = useState<Record<string, string>>(() =>
    Object.fromEntries(Object.entries(sub.limitOverrides ?? {}).map(([k, v]) => [k, String(v)])),
  );
  const [features, setFeatures] = useState<string[]>(sub.featureOverrides ?? []);

  const save = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant.id}/overrides`, {
        method: 'PATCH',
        body: {
          limitOverrides: Object.fromEntries(
            Object.entries(limits)
              .filter(([, v]) => v !== '')
              .map(([k, v]) => [k, Number(v)]),
          ),
          featureOverrides: features,
        },
      }),
    onSuccess: onDone,
  });

  const toggle = (key: string) =>
    setFeatures((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));

  return (
    <Modal
      title="استثناءات هذا المستأجر"
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            حفظ
          </button>
        </>
      }
    >
      <p className="small mute mb-4">
        ما يُمنح هنا يعلو على الباقة لهذا المستأجر وحده، ولا يتأثر بتغيير الباقة لاحقًا. يُستعمل
        للاتفاقات الخاصة لا كبديل عن ترقية الباقة.
      </p>

      <h4 className="section-title">وحدات إضافية</h4>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {FEATURES.filter((f) => !sub.plan.features.includes(f.key)).map((f) => (
          <label key={f.key} className="row pick-row" style={{ cursor: 'pointer', gap: 6 }}>
            <input
              type="checkbox"
              checked={features.includes(f.key)}
              onChange={() => toggle(f.key)}
            />
            <span className="small">{f.label}</span>
          </label>
        ))}
      </div>
      {FEATURES.every((f) => sub.plan.features.includes(f.key)) && (
        <p className="small mute">الباقة تشمل كل الوحدات المتاحة.</p>
      )}

      <h4 className="section-title">حدود مخصّصة</h4>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>الحد</th>
              <th style={{ width: 120 }}>حد الباقة</th>
              <th style={{ width: 160 }}>الممنوح</th>
            </tr>
          </thead>
          <tbody>
            {LIMIT_DEFS.map((l) => (
              <tr key={l.key}>
                <td className="cell-strong">{l.label}</td>
                <td className="num cell-mute">
                  {sub.plan.limits?.[l.key] === -1 ? 'بلا حد' : (sub.plan.limits?.[l.key] ?? '—')}
                </td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="-1"
                    aria-label={`الحد الممنوح لـ ${l.label}`}
                    placeholder="بلا استثناء"
                    value={limits[l.key] ?? ''}
                    onChange={(e) => setLimits((s) => ({ ...s, [l.key]: e.target.value }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="tiny mute">القيمة ‎-1‎ تعني بلا حد. اترك الحقل فارغًا لإلغاء الاستثناء.</span>
    </Modal>
  );
}

function InvoiceDialog({
  tenant,
  onClose,
  onDone,
}: {
  tenant: Tenant;
  onClose: () => void;
  onDone: () => void;
}) {
  const [amount, setAmount] = useState(
    tenant.subscription ? String(Number(tenant.subscription.price)) : '',
  );
  const [taxRate, setTaxRate] = useState('14');
  const [dueInDays, setDueInDays] = useState('14');

  const issue = useMutation({
    mutationFn: () =>
      api(`/tenants/${tenant.id}/invoices`, {
        method: 'POST',
        body: {
          amount: Number(amount) || 0,
          taxRate: Number(taxRate) || 0,
          dueInDays: Number(dueInDays) || 0,
        },
      }),
    onSuccess: onDone,
  });

  const net = Number(amount) || 0;
  const tax = Math.round(((net * (Number(taxRate) || 0)) / 100) * 100) / 100;

  return (
    <Modal
      title="إصدار فاتورة اشتراك"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => issue.mutate()}
            disabled={net <= 0 || issue.isPending}
          >
            إصدار
          </button>
        </>
      }
    >
      {issue.isError && (
        <div className="alert alert-danger mb-4">
          {issue.error instanceof Error ? issue.error.message : 'تعذّر الإصدار.'}
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label req" htmlFor="i-amount">
            القيمة قبل الضريبة
          </label>
          <input
            id="i-amount"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="i-tax">
            نسبة الضريبة
          </label>
          <input
            id="i-tax"
            className="input num"
            type="number"
            min="0"
            max="100"
            value={taxRate}
            onChange={(e) => setTaxRate(e.target.value)}
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="i-due">
            مهلة السداد بالأيام
          </label>
          <input
            id="i-due"
            className="input num"
            type="number"
            min="0"
            value={dueInDays}
            onChange={(e) => setDueInDays(e.target.value)}
          />
        </div>
      </div>

      <div className="totals-box mt-4">
        <div className="totals-row">
          <span className="mute">القيمة</span>
          <span className="num">{money(net)}</span>
        </div>
        <div className="totals-row">
          <span className="mute">الضريبة</span>
          <span className="num">{money(tax)}</span>
        </div>
        <div className="totals-row totals-final">
          <span>الإجمالي</span>
          <span className="num">{money(net + tax)}</span>
        </div>
      </div>
    </Modal>
  );
}
